/*
  Shoes Egy — Just landed marquee.

  The rail is a natively scrollable element and the auto-scroll advances its
  scrollLeft, rather than a CSS transform. That is the whole reason drag and
  swipe work at all: a transform animation cannot be grabbed, and overriding one
  mid-flight fights the compositor. Native scroll gives momentum, keyboard and
  trackpad panning for free, and leaves the browser in charge of the physics.

  The loop stays seamless the same way it did before: the track holds two
  identical sets, and once the rail has scrolled one full set the position is
  rewound by exactly that width. The content under the viewport is identical at
  both ends of the rewind, so nothing moves visibly.

  Auto-scroll yields to the reader — on hover, while dragging, and for a few
  seconds after they let go — so a size can be read without it sliding away.

  Also drives the homepage brand shelf (snippets/shoes-egy-brand-shelf.liquid).
  Optional attributes on the [data-marquee] element, defaults in brackets:
    data-marquee-speed   px per second [one set per 40s]
    data-marquee-reverse slide right instead of left
    data-marquee-resume  ms of quiet after a drag or touch [3500]
  Arrows: any button with data-marquee-step="1" (or "-1") and
  aria-controls set to the rail's id moves it by one item — the first set's
  first child plus the set's gap — then auto-scroll resumes after the quiet
  time. The step is animated inside the frame loop rather than with a smooth
  scrollBy, so the rewind can carry it across the seam without a jump.
  The position is kept as a float and written back each frame, because a slow
  row moves less than a pixel per frame and scrollLeft alone would round that
  away and stand still.
*/
(() => {
  const PASS_MS = 40000; // one full set, matching the old animation duration
  const RESUME_MS = 3500; // quiet time after the reader lets go
  const DRAG_SLOP = 6; // px before a press counts as a drag, not a click
  const STEP_MS = 380; // one arrow press
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const init = (root) => {
    if (root.dataset.marqueeReady) return; // the script can be included twice
    const track = root.querySelector('[data-marquee-track]');
    const sets = root.querySelectorAll('[data-marquee-set]');
    if (!track || sets.length < 2) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const speed = parseFloat(root.dataset.marqueeSpeed) || 0; // px per second
    const direction = 'marqueeReverse' in root.dataset ? -1 : 1;
    const resumeMs = parseFloat(root.dataset.marqueeResume) || RESUME_MS;
    let pos = null; // float scroll position while auto-scrolling
    let hovering = false;
    let dragging = false;
    let moved = 0;
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let resumeAt = 0;
    let last = 0;
    let step = null; // arrow animation: { from, to, start }

    const setWidth = () => sets[0].getBoundingClientRect().width;

    // keep the rail between 1px and one set width past it, so the rewind is
    // invisible in either direction (scrollLeft cannot go below 0 to wrap)
    const wrap = () => {
      const w = setWidth();
      if (w <= 0) return;
      let shift = 0;
      if (root.scrollLeft >= w + 1) shift = -w;
      else if (root.scrollLeft < 1) shift = w;
      if (!shift) return;
      root.scrollLeft += shift;
      pos = null;
      if (step) { step.from += shift; step.to += shift; }
    };

    const frame = (now) => {
      const dt = last ? Math.min(now - last, 100) : 0;
      last = now;
      const held = hovering || dragging || now < resumeAt;
      if (step) {
        const t = Math.min(1, (now - step.start) / STEP_MS);
        root.scrollLeft = step.from + (step.to - step.from) * ease(t);
        pos = null;
        if (t === 1) { step = null; resumeAt = now + resumeMs; }
      } else if (held) {
        pos = null;
      } else if (dt) {
        const w = setWidth();
        if (w > 0) {
          if (pos === null) pos = root.scrollLeft;
          pos += direction * (speed ? speed / 1000 : w / PASS_MS) * dt;
          root.scrollLeft = pos;
        }
      }
      wrap();
      requestAnimationFrame(frame);
    };

    root.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') hovering = true;
    });
    root.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') hovering = false;
    });

    /*
      Deliberately NOT setPointerCapture. Capturing the pointer on the rail
      retargets the resulting click to the rail itself, so the card's <a> never
      receives it and nothing opens — which is exactly how this was broken.
      Listening on the window instead keeps a drag alive past the rail's edges
      without stealing the click.
    */
    let activeId = null;

    const onMove = (e) => {
      if (!dragging || e.pointerId !== activeId) return;
      const dx = e.clientX - startX;
      // distance the POINTER travelled across the viewport. The rail is moving
      // under it the whole time, so anything measured against the content would
      // read a still finger as a drag.
      moved = Math.max(moved, Math.hypot(dx, e.clientY - startY));
      root.scrollLeft = startScroll - dx;
      wrap();
    };

    const onUp = (e) => {
      if (e.pointerId !== activeId) return;
      dragging = false;
      activeId = null;
      root.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      resumeAt = performance.now() + resumeMs;
    };

    root.addEventListener('pointerdown', (e) => {
      resumeAt = Infinity; // held until release
      moved = 0;
      // Touch keeps the browser's own panning — it has momentum and we do not —
      // and the browser keeps vertical page scrolling. Hold until the finger lifts.
      if (e.pointerType === 'touch' || e.button !== 0) {
        const release = () => {
          resumeAt = performance.now() + resumeMs;
          root.removeEventListener('pointerup', release);
          root.removeEventListener('pointercancel', release);
        };
        root.addEventListener('pointerup', release);
        root.addEventListener('pointercancel', release);
        return;
      }
      dragging = true;
      activeId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startScroll = root.scrollLeft;
      root.classList.add('is-dragging');
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
      // no preventDefault here — it would suppress the click that opens a card
    });

    // dragging across a card would otherwise start a native image drag
    root.addEventListener('dragstart', (e) => {
      if (dragging) e.preventDefault();
    });

    // a drag that ends on a card must not also open that card
    root.addEventListener(
      'click',
      (e) => {
        const wasDrag = moved > DRAG_SLOP;
        moved = 0; // never let one drag suppress a later click
        if (wasDrag) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    // trackpad panning and touch scrolling are reading, too
    ['wheel', 'touchmove'].forEach((t) =>
      root.addEventListener(t, () => { resumeAt = performance.now() + resumeMs; }, { passive: true })
    );
    // a drag or a swipe can reach either end with no frame loop running
    // (reduced motion), so the loop is kept endless on scroll as well
    root.addEventListener('scroll', wrap, { passive: true });

    // a focused card must be reachable and must not slide out from under a tab
    root.addEventListener('focusin', () => { resumeAt = Infinity; });
    root.addEventListener('focusout', () => { resumeAt = performance.now() + resumeMs; });

    // arrows: one item per press, presses in a row add up
    const itemWidth = () => {
      const first = sets[0].firstElementChild;
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(sets[0]).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    if (root.id) {
      document.querySelectorAll(`[data-marquee-step][aria-controls="${root.id}"]`).forEach((btn) => {
        btn.addEventListener('click', () => {
          const px = itemWidth() * (parseFloat(btn.dataset.marqueeStep) || 1);
          if (reduce.matches) { root.scrollLeft += px; wrap(); return; }
          const target = (step ? step.to : root.scrollLeft) + px;
          step = { from: root.scrollLeft, to: target, start: performance.now() };
        });
      });
    }

    root.dataset.marqueeReady = 'true';
    if (direction < 0 && root.scrollLeft < 1) root.scrollLeft = setWidth();
    if (!reduce.matches) requestAnimationFrame(frame);
  };

  const start = () => document.querySelectorAll('[data-marquee]').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
